import {IOtherHabits} from './other-habits.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';
import {omitBy as _omitBy} from 'lodash';

export interface IROtherHabits {
  otherHabits: IOtherHabits;
  wasDeployed?: boolean;
}

export namespace RecordOtherHabitsAction {
  export class GetActive {
    static readonly type = '[Record] Get Other Habits Action';
    constructor(public serviceId: string, public patientId: string) { }
  }

  export class Post {
    static readonly type = '[Record] Post OtherHabits Action';
    constructor(public serviceId: string, public patientId: string, public otherHabits: Partial<IOtherHabits>) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch Other Habits Action';
    constructor(public serviceId: string, public patientId: string, public othersHabitsId: string, public otherHabits: Partial<IOtherHabits>) { }
  }

  export class ResetState {
    static readonly type = '[Record] Reset Other Habits Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Other Habits Action';
    constructor(public value: boolean) { }
  }
}

@State<IROtherHabits>({
  name: 'OtherHabitsState',
  defaults: {
    otherHabits: null
  },
})

@Injectable()
export class OtherHabitsState {
  constructor(
    private baseService: BaseService<any>,
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getOtherHabits(state: IROtherHabits): IOtherHabits {
    return state.otherHabits;
  }

  @Selector()
  public static getWasDeployed(state: IROtherHabits): boolean {
    return state.wasDeployed;
  }

  @Action(RecordOtherHabitsAction.GetActive)
  async getOtherHabitsAction(ctx: StateContext<IROtherHabits>, action: RecordOtherHabitsAction.GetActive): Promise<void> {
    const response: DataBaseServiceResponse<IOtherHabits> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/others-habits/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      otherHabits: response.entity
    });
  }

  @Action(RecordOtherHabitsAction.Post)
  async postOtherHabitsAction(ctx: StateContext<IROtherHabits>, action: RecordOtherHabitsAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IOtherHabits> = await firstValueFrom(
      this.baseService.post(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/others-habits`, action.otherHabits)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      otherHabits: response.entity
    });
  }

  @Action(RecordOtherHabitsAction.PatchActive)
  async patchOtherHabitsAction(ctx: StateContext<IROtherHabits>, action: RecordOtherHabitsAction.PatchActive): Promise<void> {
    if (action.otherHabits.hasOwnProperty('patientId')) delete action.otherHabits.patientId;
    if (action.otherHabits.hasOwnProperty('serviceId')) delete action.otherHabits.serviceId;
    if (action.otherHabits.hasOwnProperty('timestamp')) delete action.otherHabits.timestamp;

    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/others-habits/${action.othersHabitsId}`,
        _omitBy(action.otherHabits, (prop): boolean => prop === null), // Omite nulos en el objeto enviado a servidor
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordOtherHabitsAction.ResetState)
  async resetOtherHabitsAction(ctx: StateContext<IROtherHabits>, _: RecordOtherHabitsAction.ResetState): Promise<void> {
    ctx.patchState({
      otherHabits: null,
      wasDeployed: false
    });
  }

  @Action(RecordOtherHabitsAction.Deploy)
  async wasDeployedOtherHabitsAction(ctx: StateContext<IROtherHabits>, action: RecordOtherHabitsAction.Deploy): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
